import { gql } from '@apollo/client';
import type { TypedDocumentNode } from '@apollo/client';
import type { CSSProperties } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { ManagerProgressionTaskState } from '@sorare/core/src/__generated__/globalTypes.ts';
import { Button } from '@sorare/core/src/atoms/buttons/Button/index.tsx';
import { ButtonBase } from '@sorare/core/src/atoms/buttons/ButtonBase/index.tsx';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons/index.tsx';
import { iconChevronRight } from '@sorare/core/src/atoms/icons/system/iconChevronRight.ts';
import {
  Horizontal,
  SBHorizontal,
  Vertical,
} from '@sorare/core/src/atoms/layout/flex/index.tsx';
import {
  LinkBox,
  LinkOther,
  LinkOverlay,
} from '@sorare/core/src/atoms/navigation/Box/index.tsx';
import { BrandXS, LabelL } from '@sorare/core/src/atoms/typography/index.tsx';
import { VerticalDivider } from '@sorare/core/src/atoms/ui/Divider/index.tsx';
import { NeonEdge } from '@sorare/core/src/atoms/ui/NeonEdge/index.tsx';
import { LightedBezel } from '@sorare/core/src/atoms/ui/bezels/LightedBezel/index.tsx';
import {
  AnyRewardConfig,
  Variant,
} from '@sorare/core/src/components/rewards/AnyRewardConfig/index.tsx';
import { Avatar } from '@sorare/core/src/components/user/Avatar/index.tsx';
import { TimeLeft } from '@sorare/core/src/contexts/ticker/TimeLeft/index.tsx';
import { range } from '@sorare/core/src/lib/arrays.ts';
import type { HexColor } from '@sorare/core/src/style/types.ts';

import type { TaskClaimedDialogs_taskInterface } from '#src/components/missions/TaskClaimedDialogs/__generated__/index.graphql.ts';
import { TaskClaimedDialogs } from '#src/components/missions/TaskClaimedDialogs/index.tsx';

import type { ReferralMission_inviteTaskAsReferrer } from './__generated__/index.graphql.ts';

const MissionCard = styled(SBHorizontal).attrs({ gap: 2, as: ButtonBase })`
  width: 100%;
  padding: var(--double-unit);
  border: 1px solid var(--c-nd-150);
  border-radius: var(--unit);
  background: var(--c-nl-800);
`;
const ProgressSegment = styled.div`
  flex: 1;
  height: 6px;
  border-radius: 2px;
  background: var(--segment-color);
`;
const CompletedCard = styled(LinkBox)`
  width: 100%;
`;

const Lvl = styled(BrandXS)`
  white-space: nowrap;
`;
const Nickname = styled(LabelL)`
  word-break: break-word;
`;

type Props = {
  task: ReferralMission_inviteTaskAsReferrer;
  onClick: () => void;
  onClaim: (task: TaskClaimedDialogs_taskInterface) => Promise<void>;
  onClaimed?: () => void;
  surfacePrimary: HexColor;
  surfacePrimaryText: HexColor;
};

export const ReferralMission = ({
  task,
  onClick,
  onClaim,
  onClaimed,
  surfacePrimary,
  surfacePrimaryText,
}: Props) => {
  const { progress, target, endDate, referral, steps } = task;
  const { referee } = referral;
  const completedStep = task.steps?.find(
    step => step.aasmState === ManagerProgressionTaskState.COMPLETED
  );

  const currentRewardConfig = steps?.at(progress)?.rewardConfigs?.[0];
  const currentRewardConfigUI = currentRewardConfig ? (
    <AnyRewardConfig
      anyRewardConfig={currentRewardConfig}
      variant={Variant.CHIP}
      textVariantSize="lg"
    />
  ) : null;
  if (completedStep) {
    return (
      <CompletedCard>
        <NeonEdge
          color={surfacePrimary}
          spread="60%"
          showHalo="always"
          side="top"
        >
          <LightedBezel
            color={surfacePrimary}
            borderColor={surfacePrimary}
            bezelSize={16}
            radius={16}
            background="var(--c-nl-800)"
            replaceBackground
          >
            <Horizontal
              gap={2}
              style={{ padding: 'var(--double-unit)', alignItems: 'center' }}
            >
              {currentRewardConfigUI}
              <Vertical gap={1} style={{ flex: 1 }}>
                <Horizontal gap={1}>
                  <Avatar user={referee} size={4.5} rounded />
                  <Vertical gap={0.5} className="text-left">
                    <LinkOverlay as={ButtonBase} onClick={onClick}>
                      <Nickname bold>
                        <FormattedMessage
                          defaultMessage="{nickname} completed"
                          values={{ nickname: referee.nickname }}
                        />
                      </Nickname>
                    </LinkOverlay>
                    <BrandXS color="var(--c-nd-500)">
                      <FormattedMessage
                        defaultMessage="Lvl {level}"
                        values={{ level: progress }}
                      />
                    </BrandXS>
                  </Vertical>
                </Horizontal>
                <LinkOther
                  as={Button}
                  size="small"
                  color="primary"
                  onClick={async () => {
                    await onClaim(completedStep);
                    onClaimed?.();
                  }}
                >
                  <FormattedMessage defaultMessage="Claim" />
                </LinkOther>
              </Vertical>
            </Horizontal>
          </LightedBezel>
        </NeonEdge>
      </CompletedCard>
    );
  }
  return (
    <MissionCard onClick={onClick}>
      <Vertical gap={1.5} style={{ flex: 1 }}>
        {!!endDate && (
          <BrandXS color="var(--c-nd-500)">
            <TimeLeft time={endDate} />{' '}
            <FormattedMessage defaultMessage="left" />
          </BrandXS>
        )}
        <Horizontal gap={1}>
          <Avatar user={referee} size={4.5} rounded />
          <Vertical gap={0.5}>
            <Nickname bold className="text-left">
              {referee.nickname}
            </Nickname>
          </Vertical>
        </Horizontal>
        <Horizontal gap={2} style={{ alignItems: 'center' }}>
          <Horizontal gap={1} style={{ flex: 1 }}>
            <Horizontal gap={0.5} style={{ flex: 1 }}>
              {range(target).map(i => (
                <ProgressSegment
                  key={i}
                  style={
                    {
                      '--segment-color':
                        i < progress ? surfacePrimaryText : 'var(--c-nd-50)',
                    } as CSSProperties
                  }
                />
              ))}
            </Horizontal>
            <Lvl color="var(--c-nd-600)">
              <FormattedMessage
                defaultMessage="<white>lvl {current}</white>/{total}"
                values={{
                  current: progress,
                  total: target,
                  white: chunks => (
                    <BrandXS as="span" color="var(--c-white)">
                      {chunks}
                    </BrandXS>
                  ),
                }}
              />
            </Lvl>
          </Horizontal>
          <VerticalDivider height={32} />
          {currentRewardConfigUI}
        </Horizontal>
      </Vertical>
      <FontAwesomeIcon
        icon={iconChevronRight}
        color="var(--c-nd-600)"
        size="xs"
      />
    </MissionCard>
  );
};

ReferralMission.fragments = {
  inviteTaskAsReferrer: gql`
    fragment ReferralMission_inviteTaskAsReferrer on InviteTaskAsReferrer {
      id
      aasmState
      progress
      target
      endDate
      steps {
        id
        aasmState
        rewardConfigs {
          id
          ...AnyRewardConfig_anyRewardConfigInterface
        }
        ...TaskClaimedDialogs_taskInterface
      }
      referral {
        id
        referee {
          slug
          nickname
          ...Avatar_user
        }
      }
    }
    ${
      // oxlint-disable-next-line sorare/no-detached-fragments
      TaskClaimedDialogs.fragments.taskInterface
    }
    ${AnyRewardConfig.fragments.anyRewardConfigInterface}
    ${Avatar.fragments.user}
  ` as TypedDocumentNode<ReferralMission_inviteTaskAsReferrer>,
};
